import { breakpoints } from '@awazevr/design-system.utils.react'

/**
 * Experiment-only DOM hack (avoids changing the shared search-form component):
 * open the search form's date calendar. Finds the dates field *within the
 * search form* (scoped so it never matches a property card's "7 nights") and
 * clicks it.
 *
 * On desktop the form is expanded, so we click the visible dates field directly.
 * Clicking the collapsed summary bar there opens the full mobile drawer, so the
 * summary-bar reveal is only used on mobile, where a short retry covers the
 * reveal animation.
 */
export const openDatesOverlay = (): void => {
  if (typeof document === 'undefined') {
    return
  }

  const findDateField = (root: ParentNode): HTMLElement | undefined =>
    Array.from(
      root.querySelectorAll<HTMLElement>('button, [role="button"], input'),
    ).find((el) => {
      const label =
        `${el.textContent ?? ''} ${el.getAttribute('aria-label') ?? ''} ${
          el.getAttribute('placeholder') ?? ''
        }`.toLowerCase()
      return /date|night|when/.test(label)
    })

  const openDateField = (): boolean => {
    const scopes = [
      '[data-testid="reveal-content"]',
      '[data-testid="search-form-container"]',
      '[data-testid="search-summary-button"]',
    ]
    for (const selector of scopes) {
      const root = document.querySelector<HTMLElement>(selector)
      const dateField = root ? findDateField(root) : undefined
      if (dateField) {
        dateField.click()
        dateField.focus()
        return true
      }
    }
    return false
  }

  // Desktop / already-expanded form: click the visible dates field directly.
  if (openDateField()) {
    return
  }

  // Mobile only: reveal the collapsed summary bar, then open the field. Guarded
  // to mobile so we never trigger the mobile drawer on desktop.
  const isMobile =
    typeof window !== 'undefined' && window.innerWidth < breakpoints[0]
  if (isMobile) {
    document
      .querySelector<HTMLElement>('[data-testid="search-summary-button"]')
      ?.click()
    window.setTimeout(openDateField, 150)
  }
}
